import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getClearClientModel,
  getClientFilteringOptions,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import AddOrEditClient from "./components/AddOrEditClient";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import ClientFormTabs from "./components/ClientFormTabs";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Clients() {
  const {
    clientModel,
    setClientModel,
    clientsData,
    CLIENTS_API,
    config,
    pageIndexClient,
    setPageIndexClient,
    toggleVisibleModalConfirm,
    createEntity,
    updateEntity,
    deleteEntity,
    setCity,
    setCityFilterCode,
    setMunicipalityFilterCode,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();

  const { id } = useParams();
  const [clientDataFiltered, setClientDatafiltered] = useState(
    clientsData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getClientFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const clientDataObj = clientsData.Data.find(
        (clientObj) => clientObj.Id === Number(id)
      );
      setClientModel(clientDataObj);
    }
  }, [id]);

  useEffect(() => {
    setClientDatafiltered(clientsData.Data);
  }, [clientsData]);

  const validateOperationResult = (result, isDelete) => {
    setClientModel(getClearClientModel());
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([CLIENTS_API, config]);
    if (isDelete) {
      if (pageIndexClient !== 0 && clientsData.Data.length === 1) {
        setPageIndexClient(pageIndexClient - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${CLIENTS_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    setShowDeleteConfirmation(false);
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${CLIENTS_API}/${data.Id}`}`,
      {
        ...data,
        IdentificationType: data.IdentificationType.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${CLIENTS_API}`}`,
      {
        ...data,
        IdentificationType: data.IdentificationType.Id,
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !clientModel.Name ||
    clientModel.Name.replaceAll(" ", "").length <= 0 ||
    !clientModel.Identification ||
    clientModel.Identification.replaceAll(" ", "").length <= 0 ||
    !clientModel.IdentificationType ||
    !clientModel.PhoneNumber ||
    clientModel.PhoneNumber.replaceAll(" ", "").length <= 0 ||
    !clientModel.Address ||
    clientModel.Address.replaceAll(" ", "").length <= 0 ||
    clientModel.StateId <= 0 ||
    clientModel.CityId <= 0 ||
    clientModel.clientTypeId <= 0;

  const getColumnTitles = () => {
    return [
      localize.client_type,
      localize.identification_type,
      localize.identification,
      localize.name,
      localize.address,
      localize.phone_number,
      localize.contact_name,
      localize.email,
      localize.department,
      localize.city,
      localize.zone,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.clients}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={CLIENTS_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="client"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_client}
          isEditForm
          onClickToSubmit={async () => {
            await handleUpdateRequest(clientModel);
            setShowEditModal(false);
            setCity(null);
            setCityFilterCode(null);
            setMunicipalityFilterCode(null);
            setClientModel(getClearClientModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setCity(null);
            setCityFilterCode(null);
            setMunicipalityFilterCode(null);
            setClientModel(getClearClientModel());
          }}
          Component={<ClientFormTabs />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.clients}
            subtitle={localize.clients_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => setShowPrinterModal(true)}
            entityName="client_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_client}
                onClickToSubmit={async () => {
                  await handleCreateRequest(clientModel);
                  setShowAddModal(false);
                  setCity(null);
                  setCityFilterCode(null);
                  setMunicipalityFilterCode(null);
                  setClientModel(getClearClientModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setCity(null);
                  setCityFilterCode(null);
                  setMunicipalityFilterCode(null);
                  setClientModel(getClearClientModel());
                }}
                Component={<AddOrEditClient />}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteConfirmation && (
        <DeleteConfirmationMessage
          entityName={clientModel.Name}
          entityType={localize.client}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteConfirmation(false);
            setClientModel(getClearClientModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(clientModel.Id);
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexClient}
            setPageIndex={setPageIndexClient}
            dataArray={clientsData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          clientsData.Data?.length !== 0
            ? clientDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setClientModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                          clientTypeId: item.ClientType?.Id,
                        });
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setClientModel({
                          ...item,
                          IdentificationType: {
                            Id: item.IdentificationType,
                            Name: item.IdentificationType,
                          },
                          StateId: item.State.Id,
                          CityId: item.City.Id,
                          clientTypeId: item.ClientType?.Id,
                        });
                        toggleVisibleModalConfirm();
                        setShowDeleteConfirmation(true);
                      }}
                    />
                  </div>,
                  <ValueOrNoDataLabel data={item.ClientType?.Name} />,
                  <ValueOrNoDataLabel data={item.IdentificationType} />,
                  <ValueOrNoDataLabel data={item.Identification} />,
                  <ValueOrNoDataLabel data={item.Name} />,
                  <ValueOrNoDataLabel
                    data={
                      item.AddressComplement
                        ? `${item.Address} ${item.AddressComplement ?? ""}`
                        : item.Address
                    }
                  />,
                  <ValueOrNoDataLabel data={item.PhoneNumber} />,
                  <ValueOrNoDataLabel data={item.ContactName} />,
                  <ValueOrNoDataLabel data={item.Email} />,
                  <ValueOrNoDataLabel data={item.State?.Name} />,
                  <ValueOrNoDataLabel data={item.City?.Name} />,
                  <ValueOrNoDataLabel data={item.Zone?.Name} />,
                  // item.Municipality?.Name ?? <NoDataLabel />,
                  // item.Sidewalk?.Name ?? <NoDataLabel />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(14)]
        }
      />
    </div>
  );
}
